import type { TaxonomyCategory } from '@/entities/taxonomy-category';
import type { TaxonomyFieldTemplate } from '@/entities/taxonomy-field-template';
import {
  STANDARD_FIELD_CATEGORIES,
  type StandardField,
} from '../FieldLibraryModal/standard-fields';

/**
 * The "Add field" picker's category rail and cards.
 *
 * Its own module rather than living in the component: it merges three sources
 * and the rules are worth testing directly (see `buildCatalog.test.ts`).
 */

/**
 * The default group for a template that doesn't name one.
 *
 * A category identifier, not copy: it is compared against the `category` an org
 * stored on its templates, so translating it would stop those cards grouping.
 */
// eslint-disable-next-line i18next/no-literal-string -- category identifier matched by name
export const CUSTOM = 'Custom';

/**
 * One card in the picker. Either a field from the shipped catalog or one the
 * ORG defined in the Fields tab — `template` is what distinguishes them.
 */
export interface PickerField extends StandardField {
  /** Set when this came from the org's Fields library, not the shipped catalog. */
  template?: TaxonomyFieldTemplate;
}

/** Adapts an org field template to the picker's card shape. */
function templateToPickerField(template: TaxonomyFieldTemplate): PickerField {
  return {
    // Prefixed so an org template can never collide with a catalog key.
    key: `template:${template.id}`,
    category: template.category.trim() || CUSTOM,
    name: template.name,
    group: template.group,
    type: template.type,
    ...(template.format ? { format: template.format } : {}),
    values: template.values,
    template,
  };
}

/**
 * The catalog the picker actually shows: the category REGISTRY, the shipped
 * standard fields, and the org's own Fields library, merged.
 *
 * Without the library half the two sides of the feature were disconnected — the
 * Fields tab calls itself "reusable field definitions your schemas can pull
 * from", but the builder only rendered the hardcoded frontend catalog, so a
 * field you created there was nowhere to be found and neither was its category.
 *
 * Merge rules:
 *   - The REGISTRY drives the rail: every category the org defined gets an
 *     entry, in its order, even with no fields in it yet. The Categories tab is
 *     where groupings are defined, so a category created there has to be
 *     offerable here — otherwise it exists everywhere except the one screen that
 *     puts fields into it.
 *   - An org template that was SEEDED from a catalog entry (same `libraryKey`)
 *     REPLACES it. The org's copy is the one they may have edited, so showing
 *     the shipped default alongside it would offer a stale duplicate.
 *   - An override is filed under ITS OWN category, not the shipped one it came
 *     from: re-categorising a standard field in the Fields tab has to move it
 *     here too, or the rail contradicts the library.
 *   - A template in a category with no registry row still creates one, appended
 *     after the known ones, so nothing is unreachable.
 */
export function buildCatalog(
  templates: TaxonomyFieldTemplate[],
  registry: TaxonomyCategory[],
): { category: string; fields: PickerField[] }[] {
  const overrides = new Map<string, PickerField>();
  const extras: PickerField[] = [];

  for (const template of templates) {
    const picker = templateToPickerField(template);
    if (template.libraryKey) overrides.set(template.libraryKey, picker);
    else extras.push(picker);
  }

  // Grouped case-insensitively, because a category name IS one name regardless
  // of case: the server enforces uniqueness that way and re-points templates
  // that way, so a template holding `campaign` belongs in the `Campaign` rail
  // entry rather than splitting it into a second one nobody defined.
  const byCategory = new Map<string, PickerField[]>();
  const labels = new Map<string, string>();

  /** Records how this group is spelled, first claim winning, and returns its key. */
  const claimLabel = (category: string) => {
    const key = category.trim().toLowerCase();
    if (!labels.has(key)) labels.set(key, category.trim());
    return key;
  };

  const bucketFor = (category: string) => {
    const key = claimLabel(category);
    let bucket = byCategory.get(key);
    if (!bucket) {
      bucket = [];
      byCategory.set(key, bucket);
    }
    return bucket;
  };

  // Known spellings claim their label before any template can: the registry
  // says how a category is written, then the shipped catalog, then `CUSTOM` —
  // whose exact spelling the picker compares against to find its own tab. A
  // template's spelling only names a group none of those three has heard of.
  for (const category of registry) claimLabel(category.name);
  for (const { category } of STANDARD_FIELD_CATEGORIES) claimLabel(category);
  claimLabel(CUSTOM);

  // Registry order first — it's the rail position the category record promises.
  const registered = new Set<string>();
  for (const category of registry) {
    bucketFor(category.name);
    registered.add(claimLabel(category.name));
  }

  // Then the shipped catalog, so the rail still works before the registry loads
  // (and for a shipped category the org has no row for).
  for (const { category, fields } of STANDARD_FIELD_CATEGORIES) {
    bucketFor(category);
    for (const field of fields) {
      const override = overrides.get(field.key);
      if (override) {
        overrides.delete(field.key);
        bucketFor(override.category).push(override);
      } else {
        bucketFor(category).push(field);
      }
    }
  }

  // A seeded template whose catalog entry no longer exists (renamed/removed
  // from the catalog) still belongs somewhere — treat it like a hand-made one.
  for (const orphan of Array.from(overrides.values())) bucketFor(orphan.category).push(orphan);
  for (const extra of extras) bucketFor(extra.category).push(extra);

  // An EMPTY category survives only if the org actually defined it. A shipped
  // category whose every field was re-filed elsewhere is genuinely gone.
  return Array.from(byCategory.entries())
    .filter(([key, fields]) => fields.length > 0 || registered.has(key))
    .map(([key, fields]) => ({ category: labels.get(key) ?? key, fields }));
}
